import Icon from "./Icon";
import { formatDateShort } from "../utils/formatters";

/**
 * Shared drive renderer used by the business drive and org drive. Supports
 * two view modes ("cards" / "table") and multi-select with a callback so the
 * parent owns the selection state (clears on folder change, drives bulk
 * actions). The parent passes all per-row callbacks because each drive has
 * its own ownership/permission rules.
 */
export default function DriveExplorer({
  folders = [],
  files = [],
  view = "cards",
  isSearching = false,
  canEdit = false,
  canDelete = false,
  // Whether selection checkboxes show at all. Defaults to `canDelete` so
  // existing callers (business drive) are unchanged; the org drive passes
  // `selectable` explicitly because it also offers bulk download / move,
  // which don't require delete permission.
  selectable,
  selection = { files: new Set(), folders: new Set() },
  onSelectionChange,
  onOpenFolder,
  onDownloadFile,
  onRenameFile,
  onRenameFolder,
  onMoveFile,
  onMoveFolder,
  onDeleteFile,
  onDeleteFolder,
}) {
  const canSelect = selectable ?? canDelete;
  const selectableIds = {
    files: files.map((f) => f.id),
    folders: folders.map((f) => f.id),
  };
  const totalSelectable = selectableIds.files.length + selectableIds.folders.length;
  const totalSelected = selection.files.size + selection.folders.size;
  const allSelected = totalSelectable > 0 && totalSelected === totalSelectable;
  const someSelected = totalSelected > 0 && !allSelected;

  const toggleFile = (id) => {
    const next = new Set(selection.files);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange?.({ files: next, folders: selection.folders });
  };
  const toggleFolder = (id) => {
    const next = new Set(selection.folders);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange?.({ folders: next, files: selection.files });
  };
  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange?.({ files: new Set(), folders: new Set() });
    } else {
      onSelectionChange?.({
        files: new Set(selectableIds.files),
        folders: new Set(selectableIds.folders),
      });
    }
  };

  if (view === "table") {
    return (
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          // overflow-x auto (not hidden): the fixed columns run ~640px wide,
          // so on phones the table must scroll or Actions become unreachable.
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table className="table">
          <thead>
            <tr>
              {canSelect && (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    disabled={totalSelectable === 0}
                  />
                </th>
              )}
              <th>Name</th>
              <th style={{ width: 110 }}>Type</th>
              <th style={{ width: 110 }} className="t-num">Size</th>
              <th style={{ width: 130 }}>Updated</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {folders.map((f) => {
              const selected = selection.folders.has(f.id);
              return (
                <tr
                  key={`folder-${f.id}`}
                  className="clickable"
                  onClick={() => onOpenFolder?.(f)}
                  style={selected ? { background: "var(--primary-50)" } : null}
                >
                  {canSelect && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select folder ${f.name}`}
                        checked={selected}
                        onChange={() => toggleFolder(f.id)}
                      />
                    </td>
                  )}
                  <td>
                    <span className="row gap-sm" style={{ alignItems: "center" }}>
                      <Icon name="folder" size={14} style={{ color: "var(--primary-600)" }} />
                      <span className="t-primary fw-500">{f.name}</span>
                    </span>
                  </td>
                  <td className="t-muted">Folder</td>
                  <td className="t-num t-muted">—</td>
                  <td className="t-muted">{formatDateShort(f.updated_at || f.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row gap-sm">
                      {canEdit && <button className="icon-btn" title="Rename" onClick={() => onRenameFolder?.(f)}><Icon name="edit" size={13} /></button>}
                      {canEdit && <button className="icon-btn" title="Move" onClick={() => onMoveFolder?.(f)}><Icon name="move" size={13} /></button>}
                      {canDelete && <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={() => onDeleteFolder?.(f)}><Icon name="trash" size={13} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {files.map((f) => {
              const selected = selection.files.has(f.id);
              return (
                <tr
                  key={`file-${f.id}`}
                  style={selected ? { background: "var(--primary-50)" } : null}
                >
                  {canSelect && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select file ${f.original_name}`}
                        checked={selected}
                        onChange={() => toggleFile(f.id)}
                      />
                    </td>
                  )}
                  <td>
                    <span className="row gap-sm" style={{ alignItems: "center", minWidth: 0 }}>
                      <Icon name="file-text" size={14} style={{ color: "var(--ink-500)" }} />
                      <span className="t-primary fw-500" title={f.original_name} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {f.original_name}
                      </span>
                    </span>
                  </td>
                  <td className="t-muted text-xs">{f.mime_type || "—"}</td>
                  <td className="t-num t-muted">{formatBytes(f.size)}</td>
                  <td className="t-muted">{formatDateShort(f.updated_at || f.created_at)}</td>
                  <td>
                    <div className="row gap-sm">
                      <button className="icon-btn" title="Download" onClick={() => onDownloadFile?.(f)}><Icon name="download" size={13} /></button>
                      {canEdit && <button className="icon-btn" title="Rename" onClick={() => onRenameFile?.(f)}><Icon name="edit" size={13} /></button>}
                      {canEdit && <button className="icon-btn" title="Move" onClick={() => onMoveFile?.(f)}><Icon name="move" size={13} /></button>}
                      {canDelete && <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={() => onDeleteFile?.(f)}><Icon name="trash" size={13} /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Card view
  return (
    <div>
      {canSelect && totalSelectable > 0 && (
        <div className="row" style={{ marginBottom: 10, alignItems: "center", gap: 8 }}>
          <label
            className="row gap-sm"
            style={{ alignItems: "center", cursor: "pointer", userSelect: "none" }}
          >
            <input
              type="checkbox"
              aria-label="Select all items"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
            />
            <span className="text-sm t-muted">
              {totalSelected > 0
                ? `${totalSelected} of ${totalSelectable} selected`
                : `Select all (${totalSelectable})`}
            </span>
          </label>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
        {folders.map((f) => (
          <FolderCard
            key={`folder-${f.id}`}
            folder={f}
            selected={selection.folders.has(f.id)}
            canEdit={canEdit}
            canDelete={canDelete}
            canSelect={canSelect}
            showPath={isSearching}
            onToggleSelect={() => toggleFolder(f.id)}
            onOpen={() => onOpenFolder?.(f)}
            onRename={() => onRenameFolder?.(f)}
            onMove={() => onMoveFolder?.(f)}
            onDelete={() => onDeleteFolder?.(f)}
          />
        ))}
        {files.map((f) => (
          <FileCard
            key={`file-${f.id}`}
            file={f}
            selected={selection.files.has(f.id)}
            canEdit={canEdit}
            canDelete={canDelete}
            canSelect={canSelect}
            onToggleSelect={() => toggleFile(f.id)}
            onDownload={() => onDownloadFile?.(f)}
            onRename={() => onRenameFile?.(f)}
            onMove={() => onMoveFile?.(f)}
            onDelete={() => onDeleteFile?.(f)}
          />
        ))}
      </div>
    </div>
  );
}

function SelectCheckbox({ selected, onToggle, label }) {
  return (
    <label
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: 8,
        left: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: 4,
        background: selected ? "var(--primary-600)" : "rgba(255,255,255,0.92)",
        border: `1px solid ${selected ? "var(--primary-600)" : "var(--border)"}`,
        cursor: "pointer",
        zIndex: 1,
      }}
    >
      <input
        type="checkbox"
        aria-label={label}
        checked={selected}
        onChange={onToggle}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      />
      {selected && <Icon name="check" size={12} style={{ color: "white" }} />}
    </label>
  );
}

function FolderCard({ folder, selected, showPath, canEdit, canDelete, canSelect, onToggleSelect, onOpen, onRename, onMove, onDelete }) {
  return (
    <div
      className="card"
      style={{
        padding: 14,
        cursor: "pointer",
        position: "relative",
        outline: selected ? "2px solid var(--primary-600)" : "none",
        outlineOffset: -2,
      }}
      onDoubleClick={onOpen}
    >
      {canSelect && <SelectCheckbox selected={selected} onToggle={onToggleSelect} label={`Select folder ${folder.name}`} />}
      <button
        type="button"
        onClick={onOpen}
        style={{
          background: "var(--primary-50)",
          borderRadius: 8,
          height: 72,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 10,
          cursor: "pointer",
        }}
      >
        <Icon name="folder" size={32} style={{ color: "var(--primary-600)" }} />
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="t-primary text-sm fw-600"
        title={folder.name}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {folder.name}
      </button>
      <div className="t-muted text-xs" style={{ marginTop: 2 }}>
        {showPath ? "Folder" : `Created ${formatDateShort(folder.created_at)}`}
      </div>
      <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
        {canEdit && <button className="icon-btn" onClick={onRename} title="Rename"><Icon name="edit" size={13} /></button>}
        {canEdit && <button className="icon-btn" onClick={onMove} title="Move"><Icon name="move" size={13} /></button>}
        {canDelete && <button className="icon-btn" onClick={onDelete} title="Delete" style={{ color: "var(--danger)" }}><Icon name="trash" size={13} /></button>}
      </div>
    </div>
  );
}

function FileCard({ file, selected, canEdit, canDelete, canSelect, onToggleSelect, onDownload, onRename, onMove, onDelete }) {
  return (
    <div
      className="card"
      style={{
        padding: 14,
        position: "relative",
        outline: selected ? "2px solid var(--primary-600)" : "none",
        outlineOffset: -2,
      }}
    >
      {canSelect && <SelectCheckbox selected={selected} onToggle={onToggleSelect} label={`Select file ${file.original_name}`} />}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 72, background: "var(--surface-2)", borderRadius: 8, marginBottom: 10 }}>
        <Icon name="file-text" size={28} style={{ color: "var(--ink-500)" }} />
      </div>
      <div className="t-primary text-sm fw-500" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={file.original_name}>
        {file.original_name}
      </div>
      <div className="t-muted text-xs" style={{ marginTop: 2 }}>
        {formatBytes(file.size)} · {formatDateShort(file.created_at)}
      </div>
      <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
        <button className="icon-btn" onClick={onDownload} title="Download"><Icon name="download" size={13} /></button>
        {canEdit && <button className="icon-btn" onClick={onRename} title="Rename"><Icon name="edit" size={13} /></button>}
        {canEdit && <button className="icon-btn" onClick={onMove} title="Move"><Icon name="move" size={13} /></button>}
        {canDelete && <button className="icon-btn" onClick={onDelete} title="Delete" style={{ color: "var(--danger)" }}><Icon name="trash" size={13} /></button>}
      </div>
    </div>
  );
}

export function ViewToggle({ view, onChange }) {
  return (
    <div
      className="row"
      role="tablist"
      aria-label="View mode"
      style={{
        display: "inline-flex",
        border: "1px solid var(--border)",
        borderRadius: 6,
        overflow: "hidden",
        background: "var(--surface)",
      }}
    >
      <button
        type="button"
        title="Card view"
        role="tab"
        aria-selected={view === "cards"}
        className={view === "cards" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
        style={{ borderRadius: 0 }}
        onClick={() => onChange("cards")}
      >
        <Icon name="grid" size={13} />
      </button>
      <button
        type="button"
        title="Table view"
        role="tab"
        aria-selected={view === "table"}
        className={view === "table" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
        style={{ borderRadius: 0 }}
        onClick={() => onChange("table")}
      >
        <Icon name="list" size={13} />
      </button>
    </div>
  );
}

/**
 * Sticky bar shown when 1+ items are selected. Download / Move / Delete
 * buttons each render only when their handler is supplied, so a caller can
 * offer any subset (the business drive shows Delete only; the org drive
 * shows all three, gated by permission).
 */
export function BulkActionBar({ count, onClear, onDownload, onMove, onDelete, busy }) {
  if (!count) return null;
  return (
    <div
      className="row"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 5,
        padding: "8px 12px",
        marginBottom: 12,
        background: "var(--primary-50)",
        border: "1px solid #bfdbfe",
        borderRadius: 6,
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <Icon name="check-square" size={14} style={{ color: "var(--primary-700)" }} />
      <span className="t-primary fw-600 text-sm">{count} selected</span>
      <div style={{ flex: 1 }} />
      <button className="btn btn-ghost btn-sm" onClick={onClear} disabled={busy}>
        Clear
      </button>
      {onDownload && (
        <button className="btn btn-secondary btn-sm" onClick={onDownload} disabled={busy}>
          <Icon name="download" size={12} /> Download
        </button>
      )}
      {onMove && (
        <button className="btn btn-secondary btn-sm" onClick={onMove} disabled={busy}>
          <Icon name="move" size={12} /> Move
        </button>
      )}
      {onDelete && (
        <button className="btn btn-sm" style={{ background: "var(--danger)", color: "white" }} onClick={onDelete} disabled={busy}>
          <Icon name="trash" size={12} /> {busy ? "Working…" : `Delete ${count}`}
        </button>
      )}
    </div>
  );
}

export function formatBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}
